import { isInterval } from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { DateIcon } from "@components/icon";
import { IPickerProps } from "@components/inputs/date/DatePicker";
import DatePickerBase from "@components/inputs/date/DatePickerBase";
import { TDateRangePickerValue } from "@components/inputs/date/popup/Calendar.utils";
import { MonthYearRangePopup } from "@components/inputs/date/popup/MonthYearRangePopup";
import {
    formatDateInterval,
    formatDateVariants,
    IDayInterval,
    isValidDateInterval,
    longDateFormat,
    parseDateInterval
} from "@components/inputs/date/utils";
import { WithErrorAndTooltipProps } from "@components/inputs/formError/WithErrorAndTooltip";
import { getSharedInputProps, IInputProps, InputWithErrorAndTooltip } from "@components/inputs/input";
import { withFormatter } from "@components/inputs/input/WithFormatter";
import { capitalizeWords } from "@utils/string";
import React, { useMemo } from "react";

import { DASH_CHARACTER } from "../../../constants";
import { IconSize } from "../../../enums";
import { DateFormat, getDateFormat } from "../../../types/Date";

interface IProps extends IPickerProps<IDayInterval> {
}

export const MonthRangeInput = withFormatter<IInputProps & WithErrorAndTooltipProps, IDayInterval>({
    parser: (value) => parseDateInterval(value),
    formatter: formatDateInterval,
    isValid: isValidDateInterval
}, InputWithErrorAndTooltip);

const parser = (value: string): IDayInterval => {
    const interval = parseDateInterval(value, formatDateVariants(DateFormat.monthAndYear));

    return {
        from: interval.from,
        to: interval.to,
    };
};

const formatter = (value: TDateRangePickerValue): string => {
    if (!value || !isInterval(value)) {
        return "";
    }
    return capitalizeWords(formatDateInterval(value, DateFormat.month));
};

const MonthRangePicker: React.FC<IProps> = (props) => {
    const icon = useMemo(() => <DateIcon width={IconSize.M}/>, []);

    const format = useMemo(() => {
        return longDateFormat(getDateFormat(DateFormat.month));
    }, []);

    const placeholder = useMemo(() => [format, format].join(` ${DASH_CHARACTER} `).toLowerCase(), [format]);

    const inputProps = useMemo(() => ({
        formatter,
        parser,
        isValid: isValidDateInterval
    }), []);

    const getPopupProps = useMemo(() => {
        return {
            value: props.value,
            minDate: props.minDate,
            maxDate: props.maxDate,
            isRange: true,
            hideHeader: true
        };
    }, [props.value, props.minDate, props.maxDate]);

    return (
            <DatePickerBase {...getSharedInputProps(props)}
                            value={props.value}
                            popupProps={getPopupProps}
                            width={props.width}
                            onChange={props.onChange}
                            onBlur={props.onBlur}
                            onFocus={props.onFocus}
                            onSelectionChange={props.onChange}
                            pickerPopup={MonthYearRangePopup}
                            inputComponent={MonthRangeInput}
                            inputProps={inputProps}
                            icon={icon}
                            placeholder={placeholder}
            />
    );
};

export default MonthRangePicker;